import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Fade,
  Backdrop,
  TextField,
} from "@mui/material";

import type {
  CompanyProfileData,
  UpdateDataType,
} from "@/types/companyProfile";
import type { TenantProfilePayload } from "@/types/tenantService";
import { useTenantProfileUpdate } from "@/hooks/useAdminDashboardService";
import { toast } from "react-toastify";

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "#1C1A26",
  border: "2px solid #29273A",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
  color: "#fff",
};

interface EditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; description: string }) => void;
  data: CompanyProfileData;
  refetch: () => void;
  updateNeedData: UpdateDataType;
  setUpdateNeedData: React.Dispatch<React.SetStateAction<UpdateDataType>>;
}

const EditModalAbout: React.FC<EditModalProps> = ({
  open,
  onClose,
  data,
  refetch,
}) => {
  const { mutate } = useTenantProfileUpdate();
  const tenantId = localStorage.getItem("tenantId") || "";

  const [initallData, setInitallData] = useState<TenantProfilePayload>({
    competitors: [],
    uniqueSellingPoints: [],
    targetIndustries: [],
    partners: [],
    salesMethodology: [],
    reviewText: "",
    whoweare: "",
    valueProposition: [],
    productsServices: [],
  });

  const [aboutText, setAboutText] = useState(data.about ?? "");

  useEffect(() => {
    if (data) {
      setInitallData({
        competitors: data.competitors ?? [],
        uniqueSellingPoints: data.uniqueSellingPoints ?? [],
        targetIndustries: data.targetIndustries ?? [],
        partners: data.partners ?? [],
        salesMethodology: data.salesMethodology ?? [],
        reviewText: data.reviewText ?? "",
        whoweare: data.about ?? "",
        valueProposition: data.valueProposition ?? [],
        productsServices: data.productsServices ?? [],
      });

      setAboutText(data.about ?? "");
    }
  }, [data]);

  const handleSubmit = () => {
    const payload: TenantProfilePayload = {
      whoweare: aboutText,
      productsServices: initallData.productsServices,
      valueProposition: initallData.valueProposition,
      competitors: initallData.competitors,
      uniqueSellingPoints: initallData.uniqueSellingPoints,
      partners: initallData.partners,
      targetIndustries: initallData.targetIndustries,
      salesMethodology: initallData.salesMethodology,
      reviewText: initallData.reviewText,
    };

    mutate(
      {
        id: tenantId,
        payload,
      },
      {
        onSuccess: () => {
          toast.success("Profile updated successfully");
          refetch();
          onClose();
        },
        onError: (error) => {
          toast.error("Failed to update profile");
          console.error("Update error:", error);
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Edit Section - Who We Are
          </Typography>

          <TextField
            multiline
            minRows={6}
            maxRows={12}
            fullWidth
            value={aboutText}
            onChange={(e) => setAboutText(e.target.value)}
            placeholder="Describe who you are..."
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#29273A",
                color: "#fff",
                "& fieldset": {
                  borderColor: "#777",
                },
                "&:hover fieldset": {
                  borderColor: "#B8F890",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#B8F890",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff",
              },
            }}
          />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ borderColor: "#B8F890", color: "#B8F890" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                bgcolor: "#B8F890",
                color: "#000",
                "&:hover": { bgcolor: "#A4E67E" },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default EditModalAbout;
